import { useEffect, useRef, useState } from "react";

import { showToast } from "@/utils/toastUtils";

import { BidNoticeSuggestSearchClientList } from "@/consts/Bid";
import {
  DashboardBiddingMethodList,
  DashboardContractMethodsList,
  DashboardDateRangeList,
  DashboardInternationalBidList,
  DashboardItemTypesList,
  DashboardSearchCommodity,
  DashboardSearchCompany,
  DashboardSearchProjectByTitle,
  DashboardSearchReagent,
  DefaultDashboardFilter,
  getDateRangeEndDate,
  getDateRangeStartDate,
  isYearValue,
} from "@/consts/Dashboard";

import { ValueType } from "@/types/bid";
import { DashboardFilterType } from "@/types/dashboard";

import { formattedOnlyDate, isNotEmpty, subtractMonths } from "@/libs/utils";

import { GetBidRegionList, GetLiveTextSearch } from "@/services/Bid";
import {
  CreateAgencyFilter,
  DeleteAgencyFilter,
  GetAgencyFilter,
  GetDivisionList,
} from "@/services/Dashboard";

import AccordionCheckboxPanel from "@/components/AccordionCheckboxPanel";
import AccordionPanel from "@/components/AccordionPanel";
import CommonCheckbox from "@/components/Common/Checkbox";
import CommonInput from "@/components/Common/Input";
import DashboardCompanyDivisionFilter from "@/components/DashboardCompanyDivisionFilter";
import { DateRangePicker } from "@/components/DateRangePicker";
import FilterItemLabel from "@/components/FilterItemLabel";
import FilterRadioItem from "@/components/FilterRadioItem";
import LiveTextSearch from "@/components/LiveTextSearch";
import { Slider } from "@/components/ui/slider";

type Props = {
  type: "bid" | "project";
  filter: DashboardFilterType;
  onChange: ({}: DashboardFilterType) => void;
  onSetDefault: ({}: DashboardFilterType) => void;
  onImmediateApply?: ({}: DashboardFilterType) => void;
  onBidContractorChange?: (ids: ValueType[]) => void;
};

export default function DashboardFilter({
  type,
  filter,
  onChange,
  onSetDefault,
  onImmediateApply,
  onBidContractorChange,
}: Props) {
  const isProject = type === "project";

  const [regionList, setRegionList] = useState<ValueType[]>([]);
  const [divisionList, setDivisionList] = useState<ValueType[]>([]);
  // 저장된 수요기관 목록 (API에서 불러온 값)
  const [savedDminCodes, setSavedDminCodes] = useState<ValueType[]>([]);
  // API 호출 중복 방지를 위한 ref
  const prevDminCodesRef = useRef<ValueType[]>([]);

  const [smfRatio, setSmfRatio] = useState<number>(
    DefaultDashboardFilter.smf_ratio
  );
  const handleChangeDateRange = (value: string) => {
    if (value === "0") {
      // 자유 입력
      onChange({
        date_range: value,
      } as DashboardFilterType);
    } else if (value === "YTD" || isYearValue(value)) {
      // YTD 또는 연도 선택
      onChange({
        min_date: formattedOnlyDate(getDateRangeStartDate(value).toString()),
        max_date: formattedOnlyDate(getDateRangeEndDate(value).toString()),
        date_range: value,
      } as DashboardFilterType);
    } else {
      // 기타 (이전 호환성을 위한 fallback)
      const maxDate = new Date();
      onChange({
        min_date: formattedOnlyDate(
          subtractMonths(maxDate, Number(value)).toString()
        ),
        max_date: formattedOnlyDate(maxDate.toString()),
        date_range: value,
      } as DashboardFilterType);
    }
  };

  const handleChangeDatePicker = (e: any) => {
    const newFrom = e.from;
    let newTo = e.to;

    onChange({
      min_date: formattedOnlyDate(newFrom),
      max_date: formattedOnlyDate(newTo),
    } as DashboardFilterType);
  };

  // 수요기관 변경 시 추가/삭제 API 호출 (백그라운드 처리)
  const handleDminCodesChange = (valueList: ValueType[]) => {
    const prevValues = prevDminCodesRef.current;

    // 추가된 항목 찾기
    const addedItems = valueList.filter(
      (item) => !prevValues.some((prev) => prev.value === item.value)
    );
    // 삭제된 항목 찾기
    const removedItems = prevValues.filter(
      (prev) => !valueList.some((item) => item.value === prev.value)
    );

    // ref 및 상태 먼저 업데이트 (UI 즉시 반영)
    prevDminCodesRef.current = valueList;
    setSavedDminCodes(valueList);

    // 부모 컴포넌트에 변경 알림 (필터 적용하기 클릭 시에만 실제 데이터 조회)
    onChange({
      dmin_codes: valueList,
    } as DashboardFilterType);

    // API 호출은 백그라운드에서 처리 (await 하지 않음)
    addedItems.forEach((item) => {
      CreateAgencyFilter(item.value).catch((e) => {
        console.error("Failed to create agency filter:", e);
      });
    });

    removedItems.forEach((item) => {
      DeleteAgencyFilter(item.value).catch((e) => {
        console.error("Failed to delete agency filter:", e);
      });
    });
  };

  const onLoad = async () => {
    try {
      const [regionResult, divisionResult, agencyFilterResult] =
        await Promise.all([
          GetBidRegionList(),
          GetDivisionList(),
          GetAgencyFilter(),
        ]);

      const regionItems = regionResult.data.pods[0].content.items.map(
        (item) => ({
          label: item.label,
          value: item.code,
        })
      );
      const divisionItems = divisionResult.data.pods[0].content.items.map(
        (item) => ({
          label: item.division_name,
          value: item.division_id,
        })
      );

      setRegionList(regionItems);
      setDivisionList(divisionItems);

      // 저장된 수요기관 목록 불러오기
      let dminCodeItems: ValueType[] = [];
      if (
        agencyFilterResult.data.success &&
        agencyFilterResult.data.pods[0]?.content?.items?.length > 0
      ) {
        const savedClientIds =
          agencyFilterResult.data.pods[0].content.items.map(
            (item) => item.client_id
          );

        // 각 client_id로 이름 조회
        const clientInfoPromises = savedClientIds.map(async (clientId) => {
          try {
            const result = await GetLiveTextSearch(
              BidNoticeSuggestSearchClientList,
              clientId
            );
            if (
              result.data.success &&
              result.data.pods[0]?.content?.items?.length > 0
            ) {
              const clientInfo = result.data.pods[0].content.items.find(
                (item) => item.client_id === clientId
              );
              if (clientInfo) {
                return {
                  label: clientInfo.client_name,
                  value: clientInfo.client_id,
                };
              }
            }
            // 이름을 찾지 못한 경우 client_id를 label로 사용
            return { label: clientId, value: clientId };
          } catch (e) {
            return { label: clientId, value: clientId };
          }
        });

        dminCodeItems = await Promise.all(clientInfoPromises);
        setSavedDminCodes(dminCodeItems);
        prevDminCodesRef.current = dminCodeItems;
      }

      onSetDefault({
        regions: regionItems.map((r) => r.value),
        divisions: divisionItems.map((d) => d.value),
        dmin_codes: dminCodeItems,
      } as DashboardFilterType);
    } catch (e) {
    } finally {
    }
  };

  useEffect(() => {
    onLoad();
  }, []);

  return (
    <div className="w-[320px] flex flex-col gap-2.5">
      {isProject && (
        <AccordionPanel title="프로젝트 이름">
          <LiveTextSearch
            className="min-w-full"
            placeholder="프로젝트 이름으로 검색하세요"
            defaultValue={filter.project_group_ids}
            method={DashboardSearchProjectByTitle}
            itemKey="project"
            onChange={(valueList: ValueType[]) => {
              onChange({
                project_group_ids: valueList,
              } as DashboardFilterType);
            }}
          />
        </AccordionPanel>
      )}
      {isProject && (
        <AccordionCheckboxPanel
          title="국제입찰 여부"
          data={DashboardInternationalBidList}
          defaultValue={filter.international_bid}
          onChange={(value: string[]) => {
            onChange({
              international_bid: value,
            } as DashboardFilterType);
          }}
        />
      )}
      <AccordionPanel title="공고 게시연도">
        <div className="space-y-4 text-sm text-gray-800">
          <FilterItemLabel label="공고일" />
          <DateRangePicker
            defaultValue={{
              from: new Date(filter.min_date),
              to: new Date(filter.max_date),
            }}
            disabled={filter.date_range !== "0"}
            onSelect={handleChangeDatePicker}
          />
          <FilterRadioItem
            wrapperClassName="flex justify-between"
            name="notice_date"
            defaultValue={filter.date_range}
            radioList={DashboardDateRangeList}
            onChange={handleChangeDateRange}
          />
        </div>
      </AccordionPanel>
      <AccordionPanel title="품목 구분">
        <div className="flex flex-col gap-2.5">
          <CommonCheckbox
            label="전체"
            defaultValue={
              filter.item_types.length === DashboardItemTypesList.length
            }
            onChange={(value: boolean) => {
              if (value) {
                const newItemTypes = DashboardItemTypesList.map(
                  (item) => item.value
                );
                onChange({
                  item_types: newItemTypes,
                  commodity_ids:
                    filter.commodity_ids === null ? [] : filter.commodity_ids,
                  reagent_ids:
                    filter.reagent_ids === null ? [] : filter.reagent_ids,
                } as DashboardFilterType);
              } else {
                onChange({
                  item_types: [] as string[],
                  commodity_ids: null,
                  reagent_ids: null,
                } as DashboardFilterType);
              }
            }}
          />
          <div className="flex flex-col gap-2">
            <CommonCheckbox
              label="장비"
              defaultValue={filter.item_types.includes(
                DashboardItemTypesList[0].value
              )}
              onChange={() => {
                const isEquipmentSelected = filter.item_types.includes(
                  DashboardItemTypesList[0].value
                );
                const newItemTypes = isEquipmentSelected
                  ? filter.item_types.filter(
                      (type) => type !== DashboardItemTypesList[0].value
                    )
                  : [...filter.item_types, DashboardItemTypesList[0].value];
                onChange({
                  item_types: newItemTypes,
                  commodity_ids: isEquipmentSelected
                    ? null
                    : filter.commodity_ids === null
                      ? []
                      : filter.commodity_ids,
                } as DashboardFilterType);
              }}
            />
            <LiveTextSearch
              passEmpty
              className="min-w-full"
              placeholder="장비 이름 or 품목 번호 8자리로 검색하세요"
              defaultValue={filter.commodity_ids || []}
              method={DashboardSearchCommodity}
              itemKey="product"
              onChange={(valueList: ValueType[]) => {
                onChange({
                  commodity_ids: valueList,
                } as DashboardFilterType);
              }}
            />
          </div>
          <div className="flex flex-col gap-2">
            <CommonCheckbox
              label="시약(소모품)"
              defaultValue={filter.item_types.includes(
                DashboardItemTypesList[1].value
              )}
              onChange={() => {
                const isReagentSelected = filter.item_types.includes(
                  DashboardItemTypesList[1].value
                );
                const newItemTypes = isReagentSelected
                  ? filter.item_types.filter(
                      (type) => type !== DashboardItemTypesList[1].value
                    )
                  : [...filter.item_types, DashboardItemTypesList[1].value];
                onChange({
                  item_types: newItemTypes,
                  reagent_ids: isReagentSelected
                    ? null
                    : filter.reagent_ids === null
                      ? []
                      : filter.reagent_ids,
                } as DashboardFilterType);
              }}
            />
            <LiveTextSearch
              passEmpty
              className="min-w-full"
              placeholder="시약 이름 or 품목 번호 8자리로 검색하세요"
              defaultValue={filter.reagent_ids || []}
              method={DashboardSearchReagent}
              itemKey="product"
              onChange={(valueList: ValueType[]) => {
                onChange({
                  reagent_ids: valueList,
                } as DashboardFilterType);
              }}
            />
          </div>
        </div>
      </AccordionPanel>
      <AccordionPanel title="수요 기관">
        <LiveTextSearch
          className="min-w-full"
          placeholder="수요 기관을 검색해서 입력하세요"
          defaultValue={filter.dmin_codes}
          method={BidNoticeSuggestSearchClientList}
          itemKey="client"
          onChange={handleDminCodesChange}
        />
      </AccordionPanel>
      <AccordionPanel title="지역제한" scroll>
        <div className="flex flex-col gap-2.5">
          <CommonCheckbox
            label="전체"
            defaultValue={filter.all_regions}
            onChange={(value: boolean) => {
              const newRegionList = value ? regionList.map((r) => r.value) : [];

              onChange({
                all_regions: value,
                regions: newRegionList,
              } as DashboardFilterType);
            }}
          />
          {regionList.map((region, index) => (
            <CommonCheckbox
              key={index}
              label={region.label}
              defaultValue={filter.regions.includes(region.value)}
              onChange={() => {
                const newRegionList = filter.regions.includes(region.value)
                  ? filter.regions.filter((r: string) => r !== region.value)
                  : [...filter.regions, region.value];
                const isAllRegion = newRegionList.length === regionList.length;

                onChange({
                  all_regions: isAllRegion,
                  regions: newRegionList,
                } as DashboardFilterType);
              }}
            />
          ))}
        </div>
      </AccordionPanel>
      {!isProject && (
        <AccordionCheckboxPanel
          title="입찰방식"
          data={DashboardBiddingMethodList}
          defaultValue={filter.bidding_methods}
          onChange={(value: string[]) => {
            onChange({
              bidding_methods: value,
            } as DashboardFilterType);
          }}
        />
      )}
      <AccordionCheckboxPanel
        title="계약방법"
        data={DashboardContractMethodsList}
        defaultValue={filter.contract_methods}
        onChange={(value: string[]) => {
          onChange({
            contract_methods: value,
          } as DashboardFilterType);
        }}
      />
      {!isProject && (
        <AccordionPanel title="공고 예산">
          <div className="flex flex-col gap-2">
            <div className="flex justify-between">
              <FilterItemLabel label="사업금액" />
              <CommonCheckbox
                size="sm"
                label="금액 제한 없음"
                defaultValue={filter.no_budget_limit}
                onChange={(value: boolean) => {
                  onChange({
                    no_budget_limit: value,
                  } as DashboardFilterType);
                }}
              />
            </div>
            <div className="flex items-center gap-[4px]">
              <CommonInput
                type="number"
                className="min-w-auto w-[135px]"
                disabled={filter.no_budget_limit}
                defaultValue={filter.min_budget}
                onChange={(value: number) => {
                  onChange({
                    min_budget: value,
                  } as DashboardFilterType);
                }}
                onBlur={(value: number) => {
                  if (value > filter.max_budget) {
                    showToast({
                      type: "warning",
                      message: "최소 금액은 최대 금액보다 클 수 없습니다.",
                    });
                    const newMinBudget =
                      filter.max_budget - 1 < 0 ? 0 : filter.max_budget - 1;
                    onChange({
                      min_budget: newMinBudget,
                    } as DashboardFilterType);
                  }
                }}
              />
              <span className="text-[10px] font-bold text-gray-B">~</span>
              <CommonInput
                type="number"
                className="min-w-auto w-[135px]"
                disabled={filter.no_budget_limit}
                defaultValue={filter.max_budget}
                onChange={(value: number) => {
                  onChange({
                    max_budget: value,
                  } as DashboardFilterType);
                }}
                onBlur={(value: number) => {
                  if (value < filter.min_budget) {
                    showToast({
                      type: "warning",
                      message: "최대 금액은 최소 금액보다 작을 수 없습니다.",
                    });
                    onChange({
                      max_budget: filter.min_budget + 1,
                    } as DashboardFilterType);
                  }
                }}
              />
            </div>
          </div>
        </AccordionPanel>
      )}
      {/*{!isProject && (*/}
      {/*  <AccordionCheckboxPanel*/}
      {/*    title="업종제한 여부"*/}
      {/*    data={DashboardParticipationRestrictionsList}*/}
      {/*    defaultValue={filter.participation_restrictions}*/}
      {/*    onChange={(value: string[]) => {*/}
      {/*      onChange({*/}
      {/*        participation_restrictions: value,*/}
      {/*      } as DashboardFilterType);*/}
      {/*    }}*/}
      {/*  />*/}
      {/*)}*/}
      {!isProject && (
        <DashboardCompanyDivisionFilter
          divisionList={divisionList}
          selectedCompanyTypes={filter.company_types}
          selectedDivisions={filter.divisions}
          onChange={(companyTypes: string[], divisions: string[]) => {
            onChange({
              company_types: companyTypes,
              divisions: divisions,
            } as DashboardFilterType);
          }}
        />
      )}
      {!isProject && (
        <AccordionCheckboxPanel
          title="국제입찰 여부"
          data={DashboardInternationalBidList}
          defaultValue={filter.international_bid}
          onChange={(value: string[]) => {
            onChange({
              international_bid: value,
            } as DashboardFilterType);
          }}
        />
      )}
      {!isProject && (
        <AccordionPanel title="계약기업 [입찰공고 목록에만 적용]">
          <LiveTextSearch
            className="min-w-full"
            placeholder="계약기업 이름으로 검색하세요"
            defaultValue={filter.bid_contractor_ids}
            method={DashboardSearchCompany}
            itemKey="company"
            debounceMs={300}
            onChange={(valueList: ValueType[]) => {
              onChange({
                bid_contractor_ids: valueList,
              } as DashboardFilterType);
              // 계약기업 필터는 즉시 테이블에만 적용
              onBidContractorChange?.(valueList);
            }}
          />
        </AccordionPanel>
      )}
      {isProject && (
        <AccordionPanel title="계약기업">
          <LiveTextSearch
            className="min-w-full"
            placeholder="계약기업 이름으로 검색하세요"
            defaultValue={filter.contract_company_ids}
            method={DashboardSearchCompany}
            itemKey="company"
            debounceMs={300}
            onChange={(valueList: ValueType[]) => {
              onChange({
                contract_company_ids: valueList,
              } as DashboardFilterType);
            }}
          />
        </AccordionPanel>
      )}
      {isProject && (
        <AccordionPanel title="자사제품 비중">
          <div className="space-y-4 text-sm text-gray-800">
            <div className="flex gap-[16px]">
              <Slider
                className="w-full"
                value={[smfRatio]}
                max={100}
                step={1}
                onValueChange={([value]) => {
                  setSmfRatio(value);
                }}
                onValueCommit={() => {
                  onChange({ smf_ratio: smfRatio } as DashboardFilterType);
                }}
              />
              <CommonInput
                className="w-[30px] h-[20px]"
                defaultValue={smfRatio}
                maxLength={2}
                onChange={(value) => {
                  const regExp = new RegExp(/^\s*$|^\s*\d{1,2}\s*$/);
                  regExp.test(value);
                  if (!regExp.test(value)) {
                    return;
                  }
                  setSmfRatio(value.trim());
                }}
                onBlur={() => {
                  if (isNotEmpty(smfRatio)) {
                    onChange({ smf_ratio: smfRatio } as DashboardFilterType);
                  }
                }}
              />
            </div>
          </div>
        </AccordionPanel>
      )}
    </div>
  );
}
